import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getTripsReport, downloadTripsReportPDF, downloadTripsReportExcel, TripsReportData, getAmbulances, Ambulance } from '../../services/api';
import { BOOKING_TYPE_LABELS, BOOKING_TYPE_COLORS } from '../../constants/booking';
import { getMaxDateToday, getMaxEndDateForStart } from '../../utils/dateUtils';

type DateFilterType = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

const TripsReport: React.FC = () => {
  const [trips, setTrips] = useState<TripsReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string>('');
  const [downloadError, setDownloadError] = useState<string>('');
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const ambulanceDropdownRef = useRef<HTMLDivElement>(null);
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [bookingTypeFilter, setBookingTypeFilter] = useState<string>('all');
  const [ambulanceFilter, setAmbulanceFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [ambulances, setAmbulances] = useState<Ambulance[]>([]);
  const [showAmbulanceDropdown, setShowAmbulanceDropdown] = useState(false);
  const [ambulanceSearchTerm, setAmbulanceSearchTerm] = useState('');

  // Monitor theme changes
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.documentElement.getAttribute('data-theme') === 'dark');
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };

    if (showDownloadMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDownloadMenu]);

  // Close ambulance dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ambulanceDropdownRef.current && !ambulanceDropdownRef.current.contains(event.target as Node)) {
        setShowAmbulanceDropdown(false);
        setAmbulanceSearchTerm('');
      }
    };

    if (showAmbulanceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAmbulanceDropdown]);

  // Fetch ambulances for filter
  useEffect(() => {
    const fetchAmbulances = async () => {
      try {
        const response = await getAmbulances(1, 1000);
        setAmbulances(response.data.data || []);
      } catch (err: any) {
        console.error('Failed to load ambulances:', err);
      }
    };
    fetchAmbulances();
  }, []);

  useEffect(() => {
    fetchTripsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterType, customStartDate, customEndDate, statusFilter, priorityFilter, bookingTypeFilter, ambulanceFilter, searchTerm]);

  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateFilterType) {
      case 'today': {
        const start = new Date(today);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'yesterday': {
        const start = new Date(today);
        start.setDate(start.getDate() - 1);
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastWeek': {
        const start = new Date(today);
        const dayOfWeek = start.getDay();
        const diff = start.getDate() - dayOfWeek - 6;
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(today);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'lastMonth': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      case 'custom': {
        if (!customStartDate || !customEndDate) {
          return null;
        }
        const maxEndStr = getMaxEndDateForStart(customStartDate);
        const endStr = maxEndStr && customEndDate > maxEndStr ? maxEndStr : customEndDate;
        const start = new Date(customStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endStr);
        end.setHours(23, 59, 59, 999);
        return { start: start.getTime(), end: end.getTime() };
      }
      default:
        return null;
    }
  };

  const fetchTripsData = async () => {
    try {
      setLoading(true);
      setError('');
      const dateRange = getDateRange();
      if (!dateRange) {
        setTrips([]);
        setLoading(false);
        return;
      }

      const response = await getTripsReport(
        1,
        1000,
        dateRange.start,
        dateRange.end,
        statusFilter,
        priorityFilter,
        bookingTypeFilter,
        ambulanceFilter,
        searchTerm || undefined
      );
      setTrips(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load trips data:', err);
      setError(err.response?.data?.message || 'Failed to load trips report');
      setTrips([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format: 'pdf' | 'excel') => {
    try {
      setDownloading(true);
      setDownloadError('');
      setShowDownloadMenu(false);
      
      const dateRange = getDateRange();
      if (!dateRange) {
        setDownloadError('Please select a valid date range');
        setDownloading(false);
        return;
      }
      
      let response;
      let fileExtension;
      
      if (format === 'pdf') {
        response = await downloadTripsReportPDF(
          dateRange.start,
          dateRange.end,
          statusFilter,
          priorityFilter,
          bookingTypeFilter,
          ambulanceFilter,
          searchTerm || undefined
        );
        fileExtension = 'pdf';
      } else {
        response = await downloadTripsReportExcel(
          dateRange.start,
          dateRange.end,
          statusFilter,
          priorityFilter,
          bookingTypeFilter,
          ambulanceFilter,
          searchTerm || undefined
        );
        fileExtension = 'xlsx';
      }
      
      const { url } = response.data.data;
      
      // Create a temporary anchor element to download the file
      const link = document.createElement('a');
      link.href = url;
      const dateRangeStr = dateFilterType === 'custom' 
        ? `${customStartDate}_to_${customEndDate}`
        : dateFilterType;
      link.download = `trips-report-${dateRangeStr}.${fileExtension}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error(`Error downloading ${format.toUpperCase()}:`, err);
      setDownloadError(err.response?.data?.message || `Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloading(false);
    }
  };

  const formatDateTime = (timestamp: number) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#ff9800';
      case 'assigned':
        return '#2196f3';
      case 'in_transit':
        return '#9c27b0';
      case 'patient_picked':
        return '#00bcd4';
      case 'patient_dropped':
        return '#795548';
      case 'completed':
        return '#4caf50';
      case 'cancelled':
        return '#fa003f';
      default:
        return '#999';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'medium':
        return '#2196f3';
      case 'low':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  // Filter trips based on search (already done by API, but keeping for client-side filtering if needed)
  const filteredTrips = useMemo(() => {
    return trips;
  }, [trips]);

  const filteredAmbulances = useMemo(() => {
    if (!ambulanceSearchTerm.trim()) return ambulances;
    const searchLower = ambulanceSearchTerm.toLowerCase();
    return ambulances.filter((a) => {
      const name = a.metadata.name?.toLowerCase() || '';
      const license = a.spec.licensePlate?.toLowerCase() || '';
      const type = (a.spec.type as string | undefined)?.toLowerCase() || '';
      return (
        name.includes(searchLower) ||
        license.includes(searchLower) ||
        type.includes(searchLower)
      );
    });
  }, [ambulances, ambulanceSearchTerm]);

  if (loading) {
    return (
      <div>
        <div className="loading">Loading trips report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="empty-state">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: downloadError ? '10px' : '20px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search by reference, priority, type, status, pilot, or ambulance..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
                flexShrink: 0,
                border: '1px solid',
                borderColor: isDarkTheme ? '#334155' : '#ddd',
                height: '36px',
                boxSizing: 'border-box',
                backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                color: isDarkTheme ? '#f1f5f9' : '#1e293b',
              }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="reports-filter-select"
              style={{
                padding: '8px',
                border: '1px solid',
                borderColor: isDarkTheme ? '#334155' : '#ddd',
                borderRadius: '4px',
                height: '36px',
                boxSizing: 'border-box',
                width: '105px',
                fontSize: '16px',
                backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                flexShrink: 0,
              }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="assigned">Assigned</option>
              <option value="in_transit">In Transit</option>
              <option value="patient_picked">Patient Picked</option>
              <option value="patient_dropped">Patient Dropped</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="reports-filter-select"
              style={{
                padding: '8px',
                border: '1px solid',
                borderColor: isDarkTheme ? '#334155' : '#ddd',
                borderRadius: '4px',
                height: '36px',
                boxSizing: 'border-box',
                width: '125px',
                fontSize: '16px',
                backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                flexShrink: 0,
              }}
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <select
              value={bookingTypeFilter}
              onChange={(e) => setBookingTypeFilter(e.target.value)}
              className="reports-filter-select"
              style={{
                padding: '8px',
                border: '1px solid',
                borderColor: isDarkTheme ? '#334155' : '#ddd',
                borderRadius: '4px',
                height: '36px',
                boxSizing: 'border-box',
                width: '150px',
                fontSize: '16px',
                backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                flexShrink: 0,
              }}
            >
              <option value="all">All Types</option>
              <option value="to_hospital">To Hospital</option>
              <option value="from_hospital">From Hospital</option>
            </select>
            <div ref={ambulanceDropdownRef} style={{ position: 'relative', width: '220px', flexShrink: 0 }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAmbulanceDropdown(!showAmbulanceDropdown);
                  if (!showAmbulanceDropdown) {
                    setAmbulanceSearchTerm('');
                  }
                }}
                className="custom-select-button"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid',
                  borderColor: isDarkTheme ? '#334155' : '#ddd',
                  borderRadius: '4px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '16px',
                  height: '36px',
                  boxSizing: 'border-box',
                  backgroundColor: isDarkTheme ? '#1e293b' : '#fff',
                  color: isDarkTheme ? '#f1f5f9' : '#000',
                  whiteSpace: 'nowrap',
                }}
              >
                <span
                  title={(() => {
                    if (ambulanceFilter === 'all') return 'All Ambulances';
                    const selectedAmbulance = ambulances.find((a) => a._id === ambulanceFilter);
                    return selectedAmbulance
                      ? `${selectedAmbulance.metadata.name}${selectedAmbulance.spec.licensePlate ? ` (${selectedAmbulance.spec.licensePlate})` : ''}`
                      : 'All Ambulances';
                  })()}
                >
                  {(() => {
                    if (ambulanceFilter === 'all') return 'All Ambulances';
                    const selectedAmbulance = ambulances.find((a) => a._id === ambulanceFilter);
                    if (selectedAmbulance) {
                      const fullText = `${selectedAmbulance.metadata.name}${selectedAmbulance.spec.licensePlate ? ` (${selectedAmbulance.spec.licensePlate})` : ''}`;
                      return fullText.length > 15 ? fullText.substring(0, 15) + '...' : fullText;
                    }
                    return 'All Ambulances';
                  })()}
                </span>
                <span style={{ fontSize: '14px' }}>{showAmbulanceDropdown ? '▲' : '▼'}</span>
              </button>
              {showAmbulanceDropdown && (
                <div
                  className="custom-dropdown-menu"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    width: '220px',
                    marginTop: '4px',
                    borderRadius: '6px',
                    boxShadow: isDarkTheme ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 10001,
                    maxHeight: '300px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    isolation: 'isolate',
                    backgroundColor: isDarkTheme ? '#1e293b' : '#fff',
                    border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search ambulance by name, license plate, or type..."
                    value={ambulanceSearchTerm}
                    onChange={(e) => setAmbulanceSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="search-input"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      borderBottom: `1px solid ${isDarkTheme ? '#334155' : '#ddd'}`,
                      borderRadius: '6px 6px 0 0',
                      fontSize: '16px',
                      outline: 'none',
                      backgroundColor: isDarkTheme ? '#1e293b' : '#fff',
                      color: isDarkTheme ? '#f1f5f9' : '#000',
                    }}
                    autoFocus
                  />
                  <div
                    style={{
                      maxHeight: '250px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                  >
                    {(() => {
                      if (filteredAmbulances.length === 0) {
                        return (
                          <div
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              color: isDarkTheme ? '#94a3b8' : '#666',
                              fontSize: '16px',
                            }}
                          >
                            No ambulances found
                          </div>
                        );
                      }

                      return (
                        <>
                          <div
                            onClick={() => {
                              setAmbulanceFilter('all');
                              setShowAmbulanceDropdown(false);
                              setAmbulanceSearchTerm('');
                            }}
                            className={`custom-dropdown-option ${ambulanceFilter === 'all' ? 'selected' : ''}`}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: `1px solid ${isDarkTheme ? '#334155' : '#f0f0f0'}`,
                              fontSize: '16px',
                              transition: 'background-color 0.2s',
                              backgroundColor: ambulanceFilter === 'all' ? (isDarkTheme ? '#334155' : '#f3f4f6') : 'transparent',
                              color: isDarkTheme ? '#f1f5f9' : '#000',
                            }}
                            onMouseEnter={(e) => {
                              if (ambulanceFilter !== 'all') {
                                e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f9fafb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (ambulanceFilter !== 'all') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            All Ambulances
                          </div>
                          {filteredAmbulances.map((ambulance) => {
                            const handleSelect = () => {
                              setAmbulanceFilter(ambulance._id);
                              setShowAmbulanceDropdown(false);
                              setAmbulanceSearchTerm('');
                            };

                            return (
                              <div
                                key={ambulance._id}
                                onClick={handleSelect}
                                className={`custom-dropdown-option ${ambulanceFilter === ambulance._id ? 'selected' : ''}`}
                                style={{
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  borderBottom: `1px solid ${isDarkTheme ? '#334155' : '#f0f0f0'}`,
                                  fontSize: '16px',
                                  transition: 'background-color 0.2s',
                                  backgroundColor: ambulanceFilter === ambulance._id ? (isDarkTheme ? '#334155' : '#f3f4f6') : 'transparent',
                                  color: isDarkTheme ? '#f1f5f9' : '#000',
                                }}
                                onMouseEnter={(e) => {
                                  if (ambulanceFilter !== ambulance._id) {
                                    e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f9fafb';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (ambulanceFilter !== ambulance._id) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                {ambulance.metadata.name} ({ambulance.spec.licensePlate})
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            <select
              value={dateFilterType}
              onChange={(e) => setDateFilterType(e.target.value as DateFilterType)}
              className="reports-filter-select"
              style={{
                padding: '8px',
                border: '1px solid',
                borderColor: isDarkTheme ? '#334155' : '#ddd',
                borderRadius: '4px',
                height: '36px',
                boxSizing: 'border-box',
                width: '135px',
                fontSize: '16px',
                backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                color: isDarkTheme ? '#f1f5f9' : '#1e293b',
                flexShrink: 0,
              }}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="thisWeek">This Week</option>
              <option value="lastWeek">Last Week</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
              <option value="custom">Custom Dates</option>
            </select>
            {dateFilterType === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
                <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap', flexShrink: 0 }}>Start Date:</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomStartDate(value);
                    if (value && customEndDate) {
                      const start = new Date(value);
                      const end = new Date(customEndDate);
                      const diffDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                      if (diffDays > 31) setCustomEndDate(getMaxEndDateForStart(value));
                    }
                  }}
                  max={getMaxDateToday()}
                  className="reports-date-input"
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    border: '1px solid',
                    borderColor: isDarkTheme ? '#334155' : '#ddd',
                    backgroundColor: isDarkTheme ? '#1e293b' : '#fff',
                    color: isDarkTheme ? '#f1f5f9' : '#000',
                    height: '36px',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    minWidth: '140px',
                  }}
                />
                <label className="reports-filter-label" style={{ fontSize: '16px', fontWeight: '500', whiteSpace: 'nowrap', flexShrink: 0 }}>End Date:</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate || undefined}
                  max={customStartDate ? getMaxEndDateForStart(customStartDate) : getMaxDateToday()}
                  className="reports-date-input"
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    border: '1px solid',
                    borderColor: isDarkTheme ? '#334155' : '#ddd',
                    backgroundColor: isDarkTheme ? '#1e293b' : '#fff',
                    color: isDarkTheme ? '#f1f5f9' : '#000',
                    height: '36px',
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    minWidth: '140px',
                  }}
                />
              </div>
            )}
            <div style={{ position: 'relative', flexShrink: 0 }} ref={downloadMenuRef}>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                disabled={loading || downloading || filteredTrips.length === 0}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || downloading || filteredTrips.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  opacity: loading || downloading || filteredTrips.length === 0 ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {downloading ? 'Generating...' : 'Download'}
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ marginLeft: '6px' }}
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </button>
              {showDownloadMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '5px',
                    backgroundColor: isDarkTheme ? '#1e293b' : '#ffffff',
                    border: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    minWidth: '150px',
                  }}
                >
                  <button
                    onClick={() => handleDownload('pdf')}
                    disabled={downloading}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      backgroundColor: 'transparent',
                      color: isDarkTheme ? '#ffffff' : '#333',
                      border: 'none',
                      borderRadius: '6px 6px 0 0',
                      cursor: downloading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      textAlign: 'left',
                      opacity: downloading ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!downloading) {
                        e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Download as PDF
                  </button>
                  <button
                    onClick={() => handleDownload('excel')}
                    disabled={downloading}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      backgroundColor: 'transparent',
                      color: isDarkTheme ? '#ffffff' : '#333',
                      border: 'none',
                      borderTop: isDarkTheme ? '1px solid #334155' : '1px solid #ddd',
                      borderRadius: '0 0 6px 6px',
                      cursor: downloading ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      textAlign: 'left',
                      opacity: downloading ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (!downloading) {
                        e.currentTarget.style.backgroundColor = isDarkTheme ? '#334155' : '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Download as Excel
                  </button>
                </div>
              )}
            </div>
        </div>
        {downloadError && (
          <div style={{ 
            padding: '10px', 
            backgroundColor: '#fee', 
            border: '1px solid #fcc', 
            borderRadius: '4px',
            color: '#c33',
            fontSize: '16px',
            marginBottom: '20px'
          }}>
            {downloadError}
          </div>
        )}
      </div>
      {filteredTrips.length === 0 ? (
        <div className="empty-state">
          <h3>{trips.length === 0 ? 'No trips found' : 'No matching results'}</h3>
          <p>{trips.length === 0 ? 'No trips found for the selected date range' : 'Try adjusting your search criteria'}</p>
        </div>
      ) : (
        <div className="table-container" style={{ height: 'calc(100vh - 360px)', overflowY: 'auto', overflowX: 'auto' }}>
          <table className="bookings-table">
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>Reference</th>
                <th style={{ whiteSpace: 'nowrap' }}>Priority</th>
                <th style={{ whiteSpace: 'nowrap' }}>Booking Type</th>
                <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                <th style={{ whiteSpace: 'nowrap' }}>Ambulance</th>
                <th style={{ whiteSpace: 'nowrap' }}>Pilot</th>
                <th style={{ whiteSpace: 'nowrap' }}>Distance Covered (km)</th>
                <th style={{ whiteSpace: 'nowrap' }}>Fuel Consumed (L)</th>
                <th style={{ whiteSpace: 'nowrap' }}>Booking Time</th>
                <th style={{ whiteSpace: 'nowrap' }}>Last Update</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrips.map((trip) => {
                const truncateText = (text: string, maxLength: number = 20) => {
                  if (!text || text === '—') return text;
                  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
                };

                const ambulanceText = trip.ambulance || '—';
                const pilotText = trip.pilot || '—';
                const ambulanceTruncated = truncateText(ambulanceText, 20);
                const pilotTruncated = truncateText(pilotText, 20);

                return (
                  <tr key={trip._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{trip.reference}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: 'white',
                          background: getPriorityColor(trip.priority?.toLowerCase() || 'medium'),
                        }}
                      >
                        {trip.priority?.toUpperCase() || 'MEDIUM'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: 'white',
                          background: trip.bookingType && BOOKING_TYPE_COLORS[trip.bookingType as keyof typeof BOOKING_TYPE_COLORS] 
                            ? BOOKING_TYPE_COLORS[trip.bookingType as keyof typeof BOOKING_TYPE_COLORS]
                            : '#e2e8f0',
                        }}
                      >
                        {trip.bookingType && BOOKING_TYPE_LABELS[trip.bookingType as keyof typeof BOOKING_TYPE_LABELS]
                          ? BOOKING_TYPE_LABELS[trip.bookingType as keyof typeof BOOKING_TYPE_LABELS]
                          : trip.bookingType || '—'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: 'white',
                          background: getStatusColor(trip.status?.toLowerCase() || ''),
                        }}
                      >
                        {trip.status ? trip.status.replace('_', ' ').toUpperCase() : '—'}
                      </span>
                    </td>
                    <td 
                      style={{ whiteSpace: 'nowrap' }}
                      title={ambulanceText !== ambulanceTruncated ? ambulanceText : undefined}
                    >
                      {ambulanceTruncated}
                    </td>
                    <td 
                      style={{ whiteSpace: 'nowrap' }}
                      title={pilotText !== pilotTruncated ? pilotText : undefined}
                    >
                      {pilotTruncated}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{trip.distanceCovered ? trip.distanceCovered.toFixed(2) : '0.00'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{trip.fuelConsumption ? trip.fuelConsumption.toFixed(2) : '0.00'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(trip.bookingTime)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(trip.lastUpdate)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TripsReport;
